/* eslint-disable import/no-duplicates */
/* eslint-disable no-nested-ternary */
import { experimentalStyled, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useEffect, useState, type FC, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { notificationApi } from 'src/api/notificationApi';
import useAuth from 'src/hooks/useAuth';
import logger from 'src/utils/logger';
import MissingClientContactModal from '../project/MissingClientContactModal';
import DashboardNavbar from './DashboardNavbar';
import NavbarNotificationBar from './NavbarNotificationBar';

const DRAWER_WIDTH = 280;
const DRAWER_COLLAPSED_WIDTH = 72;

interface DashboardLayoutProps {
  children?: ReactNode;
}

const DashboardLayoutRoot = experimentalStyled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  height: '100%',
  overflow: 'hidden',
  width: '100%'
}));

const DashboardLayoutWrapper = experimentalStyled('div')({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden',
  flexDirection: 'column',
  paddingTop: '64px',
  transition: 'padding-left 0.3s ease'
});

const DashboardLayoutContainer = experimentalStyled('div')({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden'
});

const DashboardLayoutContent = experimentalStyled('div')({
  flex: '1 1 auto',
  height: '100%',
  overflow: 'auto',
  position: 'relative',
  WebkitOverflowScrolling: 'touch'
});

const getMissingContactProjects = () => {
  try {
    return JSON.parse(localStorage.getItem('projects_missing_contact') || 'null') ?? [];
  } catch {
    return [];
  }
};

const shouldShowMissingContactModal = (projects: any[]): boolean => {
  if (!projects.length) return false;
  const lastShown = localStorage.getItem('missing_contact_last_prompted');
  if (!lastShown) return true;
  return Date.now() - parseInt(lastShown, 10) > 60 * 60 * 1000;
};

const DashboardLayout: FC<DashboardLayoutProps> = () => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const [missingProjects, setMissingProjects] = useState<any[]>([]);
  const [missingContactOpen, setMissingContactOpen] = useState(false);

  const checkAndShowMissingContact = () => {
    const projects = getMissingContactProjects();
    if (shouldShowMissingContactModal(projects)) {
      localStorage.setItem('missing_contact_last_prompted', Date.now().toString());
      setMissingProjects(projects);
      setMissingContactOpen(true);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      checkAndShowMissingContact();
    }
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const interval = setInterval(() => {
      checkAndShowMissingContact();
    }, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Initialize from localStorage, default to collapsed (true) if not set
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    const isKnowledgeBase = window.location.pathname.includes('knowledge_base');

    if (isKnowledgeBase) return true;
    if (savedState === null) return true;

    return savedState === 'true';
  });

  // Navbar notification state
  const [navbarNotifications, setNavbarNotifications] = useState<any[]>([]);
  const [notificationCounts, setNotificationCounts] = useState<any>(null);

  // Fetch navbar notifications
  useEffect(() => {
    const fetchNavbarNotifications = async () => {
      try {
        const res: any = await notificationApi.getAllUserNotification(0, 9, '', {
          notification_id: true,
        }, true);
        const navbarData = res?.data?.navbar;
        if (navbarData) {
          setNavbarNotifications(navbarData.items || []);
          setNotificationCounts(navbarData.counts || null);
        }
      } catch (err) {
        logger(err, 'error');
      }
    };
    fetchNavbarNotifications();
  }, []);

  // Listen for sidebar state changes
  useEffect(() => {
    const handleSidebarChange = () => {
      setSidebarCollapsed(localStorage.getItem('sidebarCollapsed') === 'true');
    };
    window.addEventListener('sidebarStateChange', handleSidebarChange);
    window.addEventListener('storage', handleSidebarChange);
    return () => {
      window.removeEventListener('sidebarStateChange', handleSidebarChange);
      window.removeEventListener('storage', handleSidebarChange);
    };
  }, []);

  // Calculate padding based on sidebar state
  const paddingLeft = isLgUp
    ? sidebarCollapsed
      ? `${DRAWER_COLLAPSED_WIDTH}px`
      : `${DRAWER_WIDTH}px`
    : '0px';

  return (
    <>
      {missingContactOpen && (
        <MissingClientContactModal
          open={missingContactOpen}
          projects={missingProjects}
          onClose={() => setMissingContactOpen(false)}
        />
      )}
      <DashboardLayoutRoot>
        <DashboardNavbar />
        <DashboardLayoutWrapper style={{ paddingLeft }}>
          {/* Navbar Notification Bar - sits between navbar and content */}
          {notificationCounts && (
          <NavbarNotificationBar
            notifications={navbarNotifications}
          />
          )}
          <DashboardLayoutContainer>
            <DashboardLayoutContent>
              <Outlet />
            </DashboardLayoutContent>
          </DashboardLayoutContainer>
        </DashboardLayoutWrapper>
      </DashboardLayoutRoot>
    </>
  );
};

export default DashboardLayout;
